import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { useSEO } from '../../hooks/useSEO';

const HomePageEn = () => {
  useSEO({
    title: 'Home',
    description: 'Personal FDM 3D Printer Research Notes. Recording and sharing filament setting data, troubleshooting guides, and measured data.',
    keywords: '3D printer,FDM,filament,PLA,PETG,ABS,troubleshooting,3D printing',
    canonicalPath: '/en'
  });

  const toEn = (path: string): string => {
    if (!path.startsWith('/') || path.startsWith('/en') || path.startsWith('/blog') || path.startsWith('http') || path.startsWith('#')) {
      return path;
    }
    return path === '/' ? '/en' : `/en${path}`;
  };

  const categoryCards = [
    {
      title: 'Filament Data',
      description: 'Settings and measured data for PLA, PETG, ABS, etc.',
      icon: 'ri-database-line',
      path: '/filament',
      accent: 'border-l-4 border-l-[#5f8d63]'
    },
    {
      title: 'Troubleshooting',
      description: 'Solutions for warping, stringing, delamination, etc.',
      icon: 'ri-tools-line',
      path: '/troubleshooting',
      accent: 'border-l-4 border-l-[#4c7ca6]'
    },
    {
      title: 'Blog',
      description: 'Material reviews, setting notes, experiment logs',
      icon: 'ri-article-line',
      path: '/blog',
      accent: 'border-l-4 border-l-[#b56b3c]'
    },
    {
      title: 'For Beginners',
      description: 'Basic knowledge and pre-purchase preparation',
      icon: 'ri-graduation-cap-line',
      path: '/beginner',
      accent: 'border-l-4 border-l-[#5f8d63]'
    },
    {
      title: 'Useful Knowledge',
      description: 'Processing, post-processing, CAD software, tool introduction',
      icon: 'ri-lightbulb-line',
      path: '/knowledge',
      accent: 'border-l-4 border-l-[#4c7ca6]'
    }
  ];

  return (
    <>
      <Header />
      <div className="min-h-screen bg-graphite mechanical-grid">
        {/* Hero Section */}
        <section className="relative py-24 px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto">
            <div className="text-center space-y-8">
              {/* Logo and Main Title */}
              <div className="space-y-6">
                {/* Large Logo Display */}
                <div className="flex justify-center mb-6">
                  <img 
                    src="/assets/nag-logo-512.png" 
                    alt="Sekisou NAG Note Logo" 
                    className="w-32 h-32 object-contain drop-shadow-2xl"
                  />
                </div>
                
                <div className="plastic-logo inline-block">
                  <h1 className="text-4xl md:text-6xl font-bold tracking-wide">
                    Sekiso NAG Note
                  </h1>
                </div>
                <div className="flex items-center justify-center space-x-4">
                  <div className="h-px bg-gradient-to-r from-transparent via-accent to-transparent w-32"></div>
                  <div className="tech-label">
                    FDM 3D Printer Technical Archive
                  </div>
                  <div className="h-px bg-gradient-to-r from-transparent via-accent to-transparent w-32"></div>
                </div>
                <p className="text-metal-light text-lg font-['Roboto_Mono'] tracking-wide">
                  Personal FDM 3D Printer Research Notes
                </p>
              </div>

              {/* Introduction Text */}
              <div className="max-w-4xl mx-auto">
                <div className="static-card p-8 rivet-decoration">
                  <div className="space-y-6">
                    <p className="text-metal-light text-lg leading-relaxed">
                      This site is a personal research notebook<br />
                      for <strong className="text-white">FDM (Fused Deposition Modeling) 3D printers</strong>.
                    </p>
                    <p className="text-metal-light leading-relaxed">
                      While organizing daily printing conditions and test results,<br />
                      I gradually compile settings, trends, and troubleshooting methods for each filament.
                    </p>
                    <p className="text-metal-light leading-relaxed">
                      All content is based on actual printing and measurements,<br />
                      recorded with the hope that it may serve as a reference for reproduction and improvement in similar environments.
                    </p>
                    <div className="pt-4">
                      <p className="text-accent text-sm font-['Roboto_Mono'] italic">
                        *Personal log of experiments and data for FDM 3D printing.*
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Category Cards Section */}
        <section className="py-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-white font-['Exo'] mb-4">
                Record Categories
              </h2>
              <div className="section-divider max-w-md mx-auto"></div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {categoryCards.map((card, index) => {
                return (
                  <Link
                    key={index}
                    to={toEn(card.path)}
                    className={`clickable-card p-6 group ${card.accent}`}
                  >
                    <div className="space-y-4">
                      <div className="flex items-center space-x-4">
                        <div className="w-14 h-14 flex items-center justify-center bg-accent text-white border border-white/20">
                          <i className={`${card.icon} text-2xl`}></i>
                        </div>
                        <h3 className="text-xl font-semibold text-white font-['Exo'] group-hover:text-accent transition-colors">
                          {card.title}
                        </h3>
                      </div>
                      <p className="text-metal-light leading-relaxed pl-18">
                        {card.description}
                      </p>
                      <div className="flex items-center text-accent text-sm font-['Roboto_Mono'] group-hover:text-white transition-colors pt-2 pl-18">
                        <span>▶ View Details</span>
                        <i className="ri-arrow-right-line ml-2 group-hover:translate-x-1 transition-transform"></i>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      </div>
      <Footer />
    </>
  );
};

export default HomePageEn;